import db from "../db/database";
import { fetchFromBusinessCentralEspecial } from "../utils/businessCentralUtils";
import { marcaConSolape } from "./margenDeSolape";
import { aRegistroLocal, emparejarPrecios } from "./identidadDePrecio";
import { anotarError, anotarLectura, anotarPaso, anotarSinConexion } from "./syncLog";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "precios de venta";

// Mínimo entre dos refrescos lanzados desde la venta.
export const INTERVALO_MINIMO_DE_REFRESCO_MS = 5 * 60 * 1000;

// Una sola lectura a la vez (tarea T2 del acuerdo de la sincronización
// incremental). La lectura se pide desde varios sitios --la sincronización
// general, el botón del listado y, por detrás, la venta-- y sin esto dos
// llamadas seguidas bajaban y aplicaban lo mismo a la vez. La segunda espera a
// la primera y recibe su resultado. El candado vale por ventana: dos ventanas de
// la app abiertas a la vez no se ven entre sí.
let lecturaEnCurso = null;

// Cuándo se lanzó el último refresco desde la venta.
let ultimoRefrescoEnSegundoPlano = 0;

// La conexión se mira directo y no con isOnline de syncService, que importa
// este archivo a través de salesPriceService: el ciclo no aporta nada.
const hayConexion = () => navigator.onLine;

// Con colchón: no la marca mayor, sino la número `FILAS_DE_SOLAPE`, para que
// un precio cuyo contador retroceda vuelva solo. Ver `margenDeSolape.js`.
const mayorContador = (filas) => marcaConSolape(filas) ?? 0;

const leerPrecios = async () => {
  if (!hayConexion()) {
    anotarSinConexion(ENTIDAD);
    return false;
  }

  try {
    const locales = await db.salesPrices.toArray();
    const desde = mayorContador(locales);

    const respuesta = await fetchFromBusinessCentralEspecial("Importaciones_getcurrentsalesprices", {
      data: JSON.stringify({ Source_Counter: desde }),
    });
    const delServidor = JSON.parse(respuesta)?.SalesPrices;
    const filas = Array.isArray(delServidor) ? delServidor : [];

    // Sin precios locales --o todos con contador 0-- la lectura es completa: se
    // vacía la tabla y se guarda de una vez. Las dos cosas van en una sola
    // transacción para que una consulta de precio a mitad no encuentre la tabla
    // vacía: ahora la venta consulta sin esperar a esta lectura.
    if (!desde) {
      const ahora = new Date();
      await db.transaction("rw", db.salesPrices, async () => {
        await db.salesPrices.clear();
        await db.salesPrices.bulkAdd(filas.map((fila) => aRegistroLocal(fila, ahora)));
      });
      anotarLectura(ENTIDAD, { guardados: filas.length });
      return true;
    }

    // Lectura incremental: cada fila se identifica por su Id o por los ocho
    // campos de la clave (identidadDePrecio.js), y todo se escribe de una vez.
    // Antes era un update por fila dentro de una transacción.
    const { registros, actualizados, nuevos, conCambiosLocales } = emparejarPrecios(locales, filas);

    if (registros.length > 0) {
      await db.salesPrices.bulkPut(registros);
    }

    anotarLectura(ENTIDAD, {
      guardados: registros.length,
      incremental: true,
      marca: Math.max(desde, mayorContador(filas)),
    });

    if (actualizados || nuevos) {
      anotarPaso(ENTIDAD, `${actualizados} actualizados · ${nuevos} nuevos`);
    }
    if (conCambiosLocales) {
      anotarPaso(ENTIDAD, `${conCambiosLocales} con cambios locales sin enviar: no se pisan`);
    }

    return true;
  } catch (error) {
    anotarError(ENTIDAD, "falló la lectura", error);
    return false;
  }
};

/**
 * Pide a Business Central los precios que cambiaron y los guarda.
 *
 * @returns {Promise<boolean>} `true` si Business Central contestó y se guardó lo que mandó.
 */
export const syncSalesPrices = () => {
  if (!lecturaEnCurso) {
    lecturaEnCurso = leerPrecios().finally(() => {
      lecturaEnCurso = null;
    });
  }

  return lecturaEnCurso;
};

/**
 * Lanza una lectura por detrás, sin esperarla, si hay conexión, no hay otra en
 * curso y ya pasaron cinco minutos desde la última lanzada así.
 *
 * Es lo que usa el cálculo del precio de una línea: antes esperaba una lectura
 * de red en cada línea, y con una lectura grande --18 MB en Ecuador-- o con un
 * relleno de contadores la caja se quedaba parada a media venta.
 *
 * Sin conexión no se apunta la hora: si no, al volver la conexión la venta
 * tardaría hasta cinco minutos en pedir los cambios.
 *
 * @returns {boolean} `true` si lanzó la lectura.
 */
export const refrescarPreciosEnSegundoPlano = (ahora = Date.now()) => {
  if (!hayConexion()) return false;
  if (lecturaEnCurso) return false;
  if (ahora - ultimoRefrescoEnSegundoPlano < INTERVALO_MINIMO_DE_REFRESCO_MS) return false;

  ultimoRefrescoEnSegundoPlano = ahora;
  syncSalesPrices().catch(() => {});
  return true;
};

// Función para obtener estadísticas de precios de venta locales
export const getSalesPricesStats = async () => {
  try {
    const allPrices = await db.salesPrices.toArray();
    const activePrices = allPrices.filter((p) => !p.deleted);

    // Agrupar por tipo de venta
    const byType = {};
    activePrices.forEach((price) => {
      const type = price.Sales_Type || "Sin tipo";
      byType[type] = (byType[type] || 0) + 1;
    });

    // Agrupar por moneda
    const byCurrency = {};
    activePrices.forEach((price) => {
      const currency = price.Currency_Code || "Sin moneda";
      byCurrency[currency] = (byCurrency[currency] || 0) + 1;
    });

    return {
      total: allPrices.length,
      active: activePrices.length,
      byType,
      byCurrency,
      lastSync:
        allPrices.length > 0
          ? Math.max(...allPrices.map((p) => p.lastSynced?.getTime() || 0))
          : null,
    };
  } catch (error) {
    anotarError(ENTIDAD, "no se pudieron obtener las estadísticas", error);
    return {
      total: 0,
      active: 0,
      byType: {},
      byCurrency: {},
      lastSync: null,
    };
  }
};

// Función para limpiar precios de venta locales (útil para testing o reset)
export const clearLocalSalesPrices = async () => {
  try {
    await db.salesPrices.clear();

    anotarPaso(ENTIDAD, "datos locales limpiados");
    return true;
  } catch (error) {
    anotarError(ENTIDAD, "no se pudieron limpiar los datos locales", error);
    return false;
  }
};

// Función para forzar sincronización de precios de venta
export const forceSyncSalesPrices = async () => {
  return await syncSalesPrices();
};
